import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import SEOHead from './SEOHead';
import Hero from './sections/Hero';
import PainPoints from './sections/PainPoints';
import DesiredOutcome from './sections/DesiredOutcome';
import ProductIntro from './sections/ProductIntro';
import ProductShowcase from './sections/ProductShowcase';
import Testimonials from './sections/Testimonials';
import TransformForm from './sections/TransformForm';
import FAQ from './sections/FAQ';
import VoiceLab from './VoiceLab';
import Pricing from './sections/Pricing';
import StickyButton from './ui/StickyButton';
import ScrollToTop from './ui/ScrollToTop';
import Footer from './sections/Footer';
import Book3D from './ui/Book3D';
import CoverEditorDemo from './CoverEditorDemo';
import { resolveGenreToStripIndex } from '../lib/covers/resolve-book-strip';
import * as FiIcons from 'react-icons/fi';
import * as BsIcons from 'react-icons/bs';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiUser, FiLogIn, FiVolume2, FiMenu, FiX, FiChevronDown, FiHeadphones, FiMic } = FiIcons;
const { BsBook, BsSoundwave } = BsIcons;

/* Grouped nav model — shared shape for desktop dropdowns + mobile accordions. */
const STUDIO_ITEMS = [
  { to: '/writing-studio', label: 'Writing Studio', desc: 'Draft & edit in your own voice' },
  { to: '/cover-studio', label: 'Cover Studio', desc: 'AI covers, KDP-ready' },
  { to: '/cover-showcase', label: 'Cover Gallery', desc: 'Browse AI cover examples' },
  { to: '/book-previewer', label: 'Book Previewer', desc: 'See your book before you publish' },
];
const AUTHOR_INTEL_ITEMS = [
  { to: '/voice-dna', label: 'Voice DNA', desc: 'Write in your own voice' },
  { to: '/author-persona', label: 'Author Persona', desc: 'A learning voice across every book' },
  { to: '/analysis', label: 'Analysis', desc: 'See your book, truly understood' },
];
const AUDIOBOOK_ITEMS = [
  { to: '/audiobook', label: 'Overview', desc: 'Ebook → ACX-ready audiobook' },
  { to: '/audiobook/acx', label: 'ACX / Audible-Ready', desc: 'Mastered to the exact spec' },
  { to: '/audiobook/voices', label: 'Narration Voices', desc: 'Natural AI narrators matched to your book' },
];

/* Desktop hover/click dropdown for a grouped nav item. */
const NavDropdown = ({ label, items, navigate }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = React.useRef(null);
  const openNow = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const closeSoon = () => { closeTimer.current = setTimeout(() => setOpen(false), 120); };
  return (
    <div className="relative" onMouseEnter={openNow} onMouseLeave={closeSoon}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 transition-colors cursor-pointer ${open ? 'text-primary-600' : 'text-gray-700 hover:text-primary-600'}`}
        aria-haspopup="true" aria-expanded={open}
      >
        {label} <SafeIcon icon={FiChevronDown} className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.15 }}
            className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-72 bg-white border border-parchment-200 rounded-2xl shadow-2xl p-2 z-50 whitespace-normal"
          >
            {items.map((it) => (
              <button
                key={it.to}
                onClick={() => { setOpen(false); navigate(it.to); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }}
                className="block w-full text-left px-4 py-3 rounded-xl hover:bg-parchment-50 transition-colors group"
              >
                <div className="text-sm font-semibold text-gray-900 group-hover:text-primary-600">{it.label}</div>
                <div className="text-xs text-gray-500">{it.desc}</div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SEOOptimizedLandingPage = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [stripGenreQuery, setStripGenreQuery] = useState('');
  const [stripMatch, setStripMatch] = useState(null);
  const STRIP_LABELS = ['Thriller', 'Romance', 'Sci-Fi', 'Fantasy', 'Literary'];
  const runStripMatch = () => setStripMatch(resolveGenreToStripIndex(stripGenreQuery, STRIP_LABELS));
  
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 100], [1, 0.8]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes or on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId) => {
    setIsMobileMenuOpen(false); // Close mobile menu
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLogoClick = () => {
    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavigateWithScroll = (path) => {
    setIsMobileMenuOpen(false);
    navigate(path);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // SEO-optimized structured data for homepage
  const homepageStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://manuscriptsmith.com/#organization",
        "name": "ManuscriptSmith",
        "url": "https://manuscriptsmith.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://manuscriptsmith.com/logo.png",
          "width": 300,
          "height": 100
        },
        "description": "AI writing studio that learns your voice and keeps your book KDP-ready - from first chapter to publish, for indie authors, ghostwriters, and self-publishers",
        "founder": {
          "@type": "Person",
          "name": "Vitaly Kirkpatrick",
          "jobTitle": "Founder & CEO"
        },
        "foundingDate": "2024",
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": "support@manuscriptsmith.com",
          "availableLanguage": "English"
        },
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "US",
          "addressRegion": "CA"
        },
        "sameAs": [
          "https://twitter.com/manuscriptsmith",
          "https://linkedin.com/company/manuscriptsmith"
        ]
      }
    ]
  };

  return (
    <HelmetProvider>
      <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
        {/* SEO Head Component */}
        <SEOHead 
          title="ManuscriptSmith - Write Like Yourself | AI Writing Studio for Authors"
          description="AI writing studio that learns your voice and keeps your book KDP-ready - from first chapter to publish. For indie authors, ghostwriters & self-publishers. See plans and get started."
          keywords="ai writing studio, author voice dna, write in your own voice, kdp preflight, kdp publishing checklist, manuscript formatting, ai book writing software, self-publishing tools, scrivener alternative, ai novel writing, manuscript export epub kdp, ghostwriting software"
          canonicalUrl="https://manuscriptsmith.com"
          structuredData={homepageStructuredData}
        />

        {/* Navigation with SEO-optimized structure */}
        <motion.nav 
          className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white/90 backdrop-blur-md shadow-sm'}`}
          style={{ opacity }}
          role="navigation"
          aria-label="Main navigation"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <motion.div 
                className="flex items-center space-x-2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <button onClick={handleLogoClick} className="flex items-center space-x-2 hover:opacity-80 transition-opacity" aria-label="ManuscriptSmith - AI Writing Studio Homepage">
                  <div className="relative flex items-center justify-center w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg shadow-lg overflow-hidden">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-8 h-8 bg-gray-900 rounded-md flex items-center justify-center relative">
                        <div className="absolute inset-0">
                          <div className="absolute top-1 left-1 w-2 h-0.5 bg-green-400"></div>
                          <div className="absolute top-1 left-1 w-0.5 h-2 bg-green-400"></div>
                          <div className="absolute top-1 right-1 w-2 h-0.5 bg-blue-400"></div>
                          <div className="absolute top-1 right-1 w-0.5 h-2 bg-blue-400"></div>
                          <div className="absolute bottom-1 left-1 w-2 h-0.5 bg-yellow-400"></div>
                          <div className="absolute bottom-1 left-1 w-0.5 h-2 bg-yellow-400"></div>
                          <div className="absolute bottom-1 right-1 w-2 h-0.5 bg-purple-400"></div>
                          <div className="absolute bottom-1 right-1 w-0.5 h-2 bg-purple-400"></div>
                        </div>
                        <div className="w-5 h-5 bg-white rounded-sm flex items-center justify-center">
                          <span className="text-[10px] font-extrabold text-primary-600">MS</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <span className="text-xl font-bold">
                    <span className="text-primary-600">Manuscript</span>
                    <span className="text-secondary-600">Smith</span>
                  </span>
                </button>
              </motion.div>

              {/* Desktop Navigation */}
              <motion.div
                className="hidden lg:flex flex-nowrap items-center gap-3.5 text-[14px] whitespace-nowrap"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <button onClick={() => handleNavigateWithScroll('/how-it-works')} className="text-gray-700 hover:text-primary-600 transition-colors cursor-pointer" aria-label="Learn how ManuscriptSmith works">
                  How It Works
                </button>
                <NavDropdown label="Studio" items={STUDIO_ITEMS} navigate={navigate} />
                <NavDropdown label="Author Intelligence" items={AUTHOR_INTEL_ITEMS} navigate={navigate} />
                <NavDropdown label="Audiobook" items={AUDIOBOOK_ITEMS} navigate={navigate} />
                <button onClick={() => handleNavigateWithScroll('/compare')} className="text-gray-700 hover:text-primary-600 transition-colors cursor-pointer" aria-label="Compare ManuscriptSmith vs Scrivener, Sudowrite, Atticus, Reedsy">
                  Compare
                </button>
                <button onClick={() => handleNavigateWithScroll('/pricing')} className="text-gray-700 hover:text-primary-600 transition-colors cursor-pointer" aria-label="View ManuscriptSmith pricing plans">
                  Pricing
                </button>
                <a href="https://app.manuscriptsmith.com/login" className="p-2 text-gray-700 hover:text-primary-600 hover:bg-gray-100 rounded-full transition-colors" title="Login to your ManuscriptSmith account" aria-label="Login to your ManuscriptSmith account">
                  <SafeIcon icon={FiUser} className="w-5 h-5" />
                </a>
                <a
                  href="https://app.manuscriptsmith.com/signup"
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-lg hover:from-primary-600 hover:to-secondary-600 transition-all transform hover:scale-105 shadow-lg"
                  aria-label="Create your ManuscriptSmith account"
                >
                  <SafeIcon icon={FiPlay} className="w-4 h-4 mr-2" /> Get Started
                </a>
              </motion.div>

              {/* Mobile Navigation */}
              <div className="lg:hidden flex items-center">
                <button onClick={toggleMobileMenu} className="p-2 text-gray-700 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Toggle mobile menu">
                  <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Menu Overlay */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <>
                {/* Backdrop */}
                <motion.div 
                  className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setIsMobileMenuOpen(false)}
                />
                {/* Mobile Menu */}
                <motion.div 
                  className="fixed top-16 right-0 w-80 max-w-[90vw] bg-white shadow-2xl z-50 lg:hidden border-l border-gray-200"
                  initial={{ x: '100%', opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: '100%', opacity: 0 }}
                  transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                >
                  <div className="p-6 h-screen overflow-y-auto">
                    {/* Get Started - Highlighted */}
                    <motion.a
                      href="https://app.manuscriptsmith.com/signup"
                      className="w-full mb-6 inline-flex items-center justify-center px-6 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-bold rounded-xl hover:from-primary-600 hover:to-secondary-600 transition-all shadow-lg"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <SafeIcon icon={FiPlay} className="w-5 h-5 mr-2" /> Get started
                    </motion.a>

                    {/* Navigation Links */}
                    <nav className="space-y-2">
                      <MobileNavLink onClick={() => handleNavigateWithScroll('/how-it-works')} icon={FiPlay} label="How It Works" description="The full studio tour" />

                      {/* Studio group */}
                      <div className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-400">Studio</div>
                      <MobileNavLink onClick={() => handleNavigateWithScroll('/writing-studio')} icon={BsBook} label="Writing Studio" description="Draft & edit in your own voice" />
                      <MobileNavLink onClick={() => handleNavigateWithScroll('/cover-studio')} icon={BsBook} label="Cover Studio" description="AI covers, KDP-ready" />
                      <MobileNavLink onClick={() => handleNavigateWithScroll('/cover-showcase')} icon={BsBook} label="Cover Gallery" description="Browse AI cover examples" />
                      <MobileNavLink onClick={() => handleNavigateWithScroll('/book-previewer')} icon={BsBook} label="Book Previewer" description="See your book before you publish" />

                      {/* Author Intelligence group */}
                      <div className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-400">Author Intelligence</div>
                      <MobileNavLink onClick={() => handleNavigateWithScroll('/voice-dna')} icon={FiVolume2} label="Voice DNA" description="Write in your own voice" />
                      <MobileNavLink onClick={() => handleNavigateWithScroll('/author-persona')} icon={FiUser} label="Author Persona" description="A learning voice across every book" />
                      <MobileNavLink onClick={() => handleNavigateWithScroll('/analysis')} icon={BsBook} label="Analysis" description="See your book, understood" />

                      {/* Audiobook group */}
                      <div className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-gray-400">Audiobook</div>
                      <MobileNavLink onClick={() => handleNavigateWithScroll('/audiobook')} icon={FiHeadphones} label="Overview" description="Ebook → ACX-ready audiobook" />
                      <MobileNavLink onClick={() => handleNavigateWithScroll('/audiobook/acx')} icon={FiHeadphones} label="ACX / Audible-Ready" description="Mastered to the exact spec" />
                      <MobileNavLink onClick={() => handleNavigateWithScroll('/audiobook/voices')} icon={FiMic} label="Narration Voices" description="Natural AI narrators matched to your book" />

                      {/* Divider */}
                      <div className="border-t border-gray-200 my-4"></div>

                      <MobileNavLink onClick={() => handleNavigateWithScroll('/compare')} icon={BsBook} label="Compare" description="vs Scrivener, Sudowrite, Reedsy" />
                      <MobileNavLink onClick={() => handleNavigateWithScroll('/pricing')} icon={FiPlay} label="Pricing" description="View our plans" />

                      {/* Divider */}
                      <div className="border-t border-gray-200 my-4"></div>

                      {/* Account Actions */}
                      <MobileNavLink onClick={() => { window.location.href = 'https://app.manuscriptsmith.com/login'; }} icon={FiUser} label="Login" description="Access your account" />
                    </nav>

                    {/* Footer Info */}
                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <div className="text-center">
                        <p className="text-sm text-gray-500 mb-2">
                          Write your book in your own voice
                        </p>
                        <div className="flex items-center justify-center space-x-4 text-xs text-gray-400">
                          <span className="flex items-center">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                            Plans from $19/mo
                          </span>
                          <span className="flex items-center">
                            <div className="w-2 h-2 bg-primary-500 rounded-full mr-1"></div>
                            Your words stay yours
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </motion.nav>

        {/* Main Content with proper semantic structure */}
        <main className="pt-16" role="main">
          <h1 className="sr-only">
            ManuscriptSmith - AI Writing Studio for Authors and Self-Publishers
          </h1>
          <Hero />
          <DesiredOutcome />
          <ProductIntro />
          <ProductShowcase />

          {/* Book Covers Studio entry point */}
          <section id="cover-studio" className="py-20 bg-gradient-to-br from-amber-50 to-rose-50 overflow-hidden">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-rose-700 bg-white px-3 py-1 rounded-full mb-4 shadow-sm">
                  Cover Studio
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  AI-designed covers that readers actually click on
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Describe your story, get 5 cover directions in 30 seconds. Edit any element — change just the title,
                  just the colors — without redoing the whole design.
                </p>
              </div>

              {/* Type your own genre — matches to the closest book below (same keyword-match
                  + honest-fallback logic as cover-showcase / cover-studio / the real backend). */}
              <div className="max-w-lg mx-auto mb-8">
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    value={stripGenreQuery}
                    onChange={(e) => setStripGenreQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && runStripMatch()}
                    placeholder="Type your genre — e.g. &quot;cozy mystery&quot;, &quot;space opera&quot;"
                    maxLength={80}
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-primary-500"
                  />
                  <button type="button" onClick={runStripMatch} disabled={!stripGenreQuery.trim()}
                    className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-gradient-to-r from-rose-500 to-amber-500 text-white hover:from-rose-600 hover:to-amber-600 disabled:opacity-50 whitespace-nowrap">
                    Match my genre
                  </button>
                </div>
                {stripMatch && (
                  <p className="text-[12px] text-gray-500 mt-1.5 text-center">
                    {stripMatch.matched
                      ? <>Closest match: <span className="font-semibold text-rose-600">{stripMatch.label}</span></>
                      : <>No exact match for "{stripGenreQuery}" — showing <span className="font-semibold text-rose-600">Literary</span>, same default the real app uses.</>
                    }
                  </p>
                )}
              </div>

              {/* Horizontal scroll strip of genre books */}
              <div className="flex items-end justify-center gap-8 md:gap-12 overflow-x-auto pb-6 scrollbar-none">
                <div className="flex-shrink-0 flex flex-col items-center gap-3">
                  <div className={stripMatch?.index === 0 ? 'ring-2 ring-rose-500 rounded-md ring-offset-4 ring-offset-amber-50' : ''}>
                    <Book3D
                      title="The Last Signal"
                      author="M. Crane"
                      palette={{ bg: 'linear-gradient(150deg,#1c1917,#450a0a)', accent: '#ef4444', text: '#fafafa' }}
                      coverImage="/img/cover-examples/cold-precision.jpg"
                      width={140}
                      angle={-22}
                    />
                  </div>
                  <span className={`text-xs font-semibold uppercase tracking-wide ${stripMatch?.index === 0 ? 'text-rose-600' : 'text-gray-500'}`}>Thriller</span>
                </div>
                <div className="flex-shrink-0 flex flex-col items-center gap-3">
                  <div className={stripMatch?.index === 1 ? 'ring-2 ring-rose-500 rounded-md ring-offset-4 ring-offset-amber-50' : ''}>
                    <Book3D
                      title="A Thousand Dawns"
                      author="S. Ellery"
                      palette={{ bg: 'linear-gradient(150deg,#4c1d6e,#f9a8d4)', accent: '#fbbf24', text: '#fdf4ff' }}
                      coverImage="/img/cover-examples/warm-bloom.jpg"
                      width={140}
                      angle={-22}
                    />
                  </div>
                  <span className={`text-xs font-semibold uppercase tracking-wide ${stripMatch?.index === 1 ? 'text-rose-600' : 'text-gray-500'}`}>Romance</span>
                </div>
                <div className="flex-shrink-0 flex flex-col items-center gap-3">
                  <div className={stripMatch?.index === 2 ? 'ring-2 ring-rose-500 rounded-md ring-offset-4 ring-offset-amber-50' : ''}>
                    <Book3D
                      title="Void Protocol"
                      author="J. Keller"
                      palette={{ bg: 'linear-gradient(150deg,#0f172a,#164e63)', accent: '#22d3ee', text: '#e0f2fe' }}
                      coverImage="/img/cover-examples/void-protocol.jpg"
                      width={160}
                      angle={-18}
                    />
                  </div>
                  <span className={`text-xs font-semibold uppercase tracking-wide ${stripMatch?.index === 2 ? 'text-rose-600' : 'text-gray-500'}`}>Sci-Fi</span>
                </div>
                <div className="flex-shrink-0 flex flex-col items-center gap-3">
                  <div className={stripMatch?.index === 3 ? 'ring-2 ring-rose-500 rounded-md ring-offset-4 ring-offset-amber-50' : ''}>
                    <Book3D
                      title="The Iron Crown"
                      author="A. Voss"
                      palette={{ bg: 'linear-gradient(150deg,#1c0f3d,#713f12)', accent: '#d4a017', text: '#fef3c7' }}
                      coverImage="/img/cover-examples/epic-dark.jpg"
                      width={140}
                      angle={-22}
                    />
                  </div>
                  <span className={`text-xs font-semibold uppercase tracking-wide ${stripMatch?.index === 3 ? 'text-rose-600' : 'text-gray-500'}`}>Fantasy</span>
                </div>
                <div className="flex-shrink-0 flex flex-col items-center gap-3">
                  <div className={stripMatch?.index === 4 ? 'ring-2 ring-rose-500 rounded-md ring-offset-4 ring-offset-amber-50' : ''}>
                    <Book3D
                      title="Before the Rain"
                      author="C. Moss"
                      palette={{ bg: 'linear-gradient(150deg,#f5f0e8,#e7e0d0)', accent: '#1e293b', text: '#1e293b' }}
                      coverImage="/img/cover-examples/atmospheric-full.jpg"
                      width={140}
                      angle={-22}
                    />
                  </div>
                  <span className={`text-xs font-semibold uppercase tracking-wide ${stripMatch?.index === 4 ? 'text-rose-600' : 'text-gray-500'}`}>Literary</span>
                </div>
              </div>

              {/* Direct-edit demo — placement / typography / colors + a real live AI edit, right here */}
              <div className="mt-14 rounded-2xl bg-white/70 border border-gray-200 p-6 md:p-8 max-w-3xl mx-auto">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-1.5">Move the title, swap the typeface, pick new colors — or just ask</h3>
                  <p className="text-sm text-gray-500">Try it right here, live — the same controls and the same AI edit as Cover Studio.</p>
                </div>
                <CoverEditorDemo />
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
                <button
                  onClick={() => handleNavigateWithScroll('/cover-showcase')}
                  className="inline-flex items-center px-7 py-3.5 border-2 border-gray-900 text-gray-900 font-bold rounded-lg hover:bg-gray-900 hover:text-white transition-all"
                >
                  Explore the Cover Gallery
                </button>
                <button
                  onClick={() => handleNavigateWithScroll('/cover-studio')}
                  className="inline-flex items-center px-7 py-3.5 bg-gradient-to-r from-rose-500 to-amber-500 text-white font-bold rounded-lg hover:from-rose-600 hover:to-amber-600 transition-all shadow-lg"
                >
                  <SafeIcon icon={FiPlay} className="w-4 h-4 mr-2" /> Open Cover Studio
                </button>
              </div>
            </div>
          </section>

          {/* Your Author AI entry point */}
          <section className="py-16 bg-gradient-to-br from-emerald-50 to-stone-100">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-emerald-700 bg-white px-3 py-1 rounded-full mb-4">
                ManuscriptSmith exclusive
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Your Author AI knows <em>you</em></h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
                Feed it your life — documents, Drive links, forwarded emails, whole archives, even your photo library — and it plans and
                writes from your real facts, in your voice. Sensitive details stay visible to you and redacted from
                every AI until you authorize each one.
              </p>
              <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-8 text-left">
                {[['🧠','Knows your life','Your facts ground every suggestion'],['🛡','Guards your secrets','Per-item privacy vault, reversible'],['🔍','Fully auditable','Test exactly what your AI knows']].map(([i,t,d]) => (
                  <div key={t} className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="text-2xl mb-1">{i}</div>
                    <div className="font-semibold text-gray-900 text-sm">{t}</div>
                    <div className="text-xs text-gray-600">{d}</div>
                  </div>
                ))}
              </div>
              <button onClick={() => handleNavigateWithScroll('/author-ai')}
                className="inline-flex items-center px-8 py-4 bg-emerald-700 text-white font-bold text-lg rounded-lg hover:bg-emerald-800 transition-all shadow-lg">
                Meet your Author AI <SafeIcon icon={FiPlay} className="w-5 h-5 ml-2" />
              </button>
            </div>
          </section>

          {/* Analysis showcase entry point */}
          <section className="py-16 bg-gradient-to-br from-parchment-100 to-secondary-50">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-secondary-700 bg-white px-3 py-1 rounded-full mb-4">
                Manuscript Analysis
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">See your book, truly understood</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
                We read a manuscript the way a great editor does — your story, your themes, the fingerprint of your
                voice, even whether it reads as authentically human. Explore real sample reports across genres.
              </p>
              <div className="flex justify-center -space-x-3 mb-8">
                {['from-sky-500 to-amber-500','from-rose-400 to-secondary-500','from-primary-400 to-primary-600','from-slate-500 to-slate-700','from-secondary-400 to-amber-600'].map((c, i) => (
                  <div key={i} className={`w-12 h-16 rounded-lg bg-gradient-to-br ${c} shadow-md border-2 border-white`} style={{ transform: `rotate(${(i - 2) * 4}deg)` }} />
                ))}
              </div>
              <button onClick={() => handleNavigateWithScroll('/analysis')}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-bold text-lg rounded-lg hover:from-primary-600 hover:to-secondary-600 transition-all shadow-lg">
                Explore sample analyses <SafeIcon icon={FiPlay} className="w-5 h-5 ml-2" />
              </button>
            </div>
          </section>

          {/* Author Persona entry point — the learning, cross-book voice engine */}
          <section className="py-16 bg-white">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-primary-700 bg-primary-50 px-3 py-1 rounded-full mb-4">
                Your Author Persona
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">It learns who you are — and writes like you</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
                ManuscriptSmith builds a private persona from your own writing and background, sharpens it as you add
                more of your work, and keeps it on your account — and unlike other AI, it never makes things up.
              </p>
              <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-8 text-left">
                {[
                  ['Built from you', 'Your background + voice, drawn from your real work — never generic.'],
                  ['Sharpens with your work', 'Add more of your writing and the profile gets more you over time.'],
                  ['Never fabricated', 'Real facts only. The only invented thing allowed is your own fiction.'],
                ].map(([t, d]) => (
                  <div key={t} className="rounded-2xl border border-gray-200 bg-parchment-50 p-5">
                    <div className="font-bold text-gray-900 mb-1">{t}</div>
                    <div className="text-sm text-gray-600">{d}</div>
                  </div>
                ))}
              </div>
              <button onClick={() => handleNavigateWithScroll('/author-persona')}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-bold text-lg rounded-lg hover:from-primary-600 hover:to-secondary-600 transition-all shadow-lg">
                See how your Author Persona works <SafeIcon icon={FiPlay} className="w-5 h-5 ml-2" />
              </button>
            </div>
          </section>

          <TransformForm />
          <FAQ />
          <Pricing />

          {/* Audiobook — the second pillar: write → narrate → publish */}
          <section className="py-20 bg-gradient-to-br from-gray-900 to-primary-900 text-white overflow-hidden">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-secondary-300 bg-white/10 px-3 py-1 rounded-full mb-4">
                  The second pillar · Write → Narrate → Publish
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-3">Finished the book? Now hear it.</h2>
                <p className="text-lg opacity-90 max-w-2xl mx-auto">
                  Your finished, KDP-ready manuscript turns into <span className="font-semibold text-secondary-300">ACX / Audible-spec audio</span> —
                  chapter by chapter, in a natural AI narrator voice matched to your book. One book people can read <em>and</em> listen to.
                </p>
              </div>

              {/* animated waveform */}
              <div className="flex items-end justify-center gap-1 h-20 mb-12">
                {[...Array(40)].map((_, i) => (
                  <div key={i} className="w-1.5 rounded-full bg-gradient-to-t from-primary-400 to-secondary-400"
                    style={{ height: `${25 + ((i * 3) % 7) * 11}%` }} />
                ))}
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-10">
                {[
                  ['Chapter narration', 'Consistent tone, pacing, and pronunciation across every chapter — driven by your finished book.'],
                  ['ACX / Audible-ready', 'Mastered to the exact loudness, peak, and noise-floor spec Audible checks — with a compliance report.'],
                  ['Narration voices', 'Natural narrators for every genre — audition them and pick the read that fits your book.'],
                ].map(([t, d]) => (
                  <div key={t} className="rounded-2xl bg-white/5 border border-white/10 p-6 text-left">
                    <div className="font-bold text-white mb-1">{t}</div>
                    <div className="text-sm opacity-80">{d}</div>
                  </div>
                ))}
              </div>

              <div className="text-center">
                <p className="text-sm opacity-70 max-w-2xl mx-auto mb-6">
                  Files are ready for Amazon Virtual Voice (KDP) and the sanctioned wide-distribution pipelines —
                  we keep the claims honest. Audiobook narration is an optional add-on; the writing studio stays focused on writing.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <button
                    onClick={() => handleNavigateWithScroll('/audiobook')}
                    className="inline-flex items-center px-7 py-3.5 bg-white text-primary-700 font-bold rounded-lg hover:bg-gray-50 transition-all"
                  >
                    Explore the audiobook studio
                  </button>
                  <button
                    onClick={() => handleNavigateWithScroll('/audiobook/acx')}
                    className="inline-flex items-center px-7 py-3.5 border border-white/40 text-white font-semibold rounded-lg hover:bg-white/10 transition-all"
                  >
                    ACX / Audible-ready details
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Studio Access CTA */}
          <section className="py-20 bg-gradient-to-br from-emerald-900 to-teal-800 text-white">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-teal-300 bg-white/10 px-3 py-1 rounded-full mb-6">
                Writing Studio
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Your manuscript is waiting</h2>
              <p className="text-lg text-emerald-100 max-w-xl mx-auto mb-10">
                The full writing studio — Voice DNA, chapter editor, cover generator, analysis, publish tools.
                See plans and get started.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://write.audiobooksmith.com/studio/"
                  className="inline-flex items-center px-8 py-4 bg-white text-emerald-900 font-bold text-lg rounded-lg hover:bg-emerald-50 transition-all shadow-xl"
                >
                  <SafeIcon icon={FiPlay} className="w-5 h-5 mr-2" /> Open the Writing Studio
                </a>
                <button
                  onClick={() => handleNavigateWithScroll('/editor')}
                  className="inline-flex items-center px-6 py-4 border border-white/40 text-white font-semibold rounded-lg hover:bg-white/10 transition-all"
                >
                  Watch a demo
                </button>
              </div>
            </div>
          </section>

          <Footer />
        </main>

        {/* Sticky CTA Button */}
        <StickyButton />

        {/* Scroll to Top Button */}
        <ScrollToTop />

        {/* Login Modal */}
        {showLoginModal && (
          <div 
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-modal-title"
          >
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <h2 id="login-modal-title" className="text-2xl font-bold text-gray-900 mb-6">
                Login to Your Account
              </h2>
              <form className="space-y-4">
                <div>
                  <label htmlFor="modal-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input 
                    id="modal-email"
                    type="email" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" 
                    placeholder="Enter your email" 
                    aria-describedby="email-help"
                  />
                </div>
                <div>
                  <label htmlFor="modal-password" className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input 
                    id="modal-password"
                    type="password" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" 
                    placeholder="Enter your password" 
                  />
                </div>
                <div className="flex justify-between items-center text-sm">
                  <div className="flex items-center">
                    <input type="checkbox" id="modal-remember" className="mr-2" />
                    <label htmlFor="modal-remember">Remember me</label>
                  </div>
                  <a href="#" className="text-primary-600 hover:text-primary-800">
                    Forgot password?
                  </a>
                </div>
                <button className="w-full bg-primary-600 text-white py-2 rounded-lg hover:bg-primary-700">
                  Login
                </button>
              </form>
              <div className="text-center mt-4">
                <p className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <button 
                    onClick={() => {
                      setShowLoginModal(false);
                      handleNavigateWithScroll('/signup');
                    }} 
                    className="text-primary-600 hover:text-primary-800"
                  >
                    Sign up here
                  </button>
                </p>
              </div>
              <button 
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700" 
                onClick={() => setShowLoginModal(false)}
                aria-label="Close login modal"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </HelmetProvider>
  );
};

// Mobile Navigation Link Component
const MobileNavLink = ({ onClick, icon, label, description }) => (
  <motion.button
    onClick={onClick}
    className="w-full flex items-center p-4 text-left hover:bg-gray-50 rounded-xl transition-colors group"
    whileHover={{ x: 4 }}
    whileTap={{ scale: 0.98 }}
  >
    <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-primary-200 transition-colors">
      <SafeIcon icon={icon} className="w-5 h-5 text-primary-600" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-gray-900">{label}</div>
      <div className="text-xs text-gray-500 truncate">{description}</div>
    </div>
    <div className="flex-shrink-0 ml-2">
      <svg className="w-4 h-4 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  </motion.button>
);

export default SEOOptimizedLandingPage;